import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Slide,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  COOKIE_CONSENT_EVENT,
  COOKIE_CONSENT_OPEN_EVENT,
  getCookieConsent,
  getDefaultCookiePreferences,
  saveCookieConsent,
} from '../utils/cookieConsent';

const TransitionUp = React.forwardRef(function TransitionUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function CookieConsentBanner() {
  const [isBannerOpen, setIsBannerOpen] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [preferences, setPreferences] = useState(getDefaultCookiePreferences());

  useEffect(() => {
    const existing = getCookieConsent();

    if (existing) {
      setPreferences(existing);
      setIsBannerOpen(false);
      return;
    }

    setIsBannerOpen(true);
  }, []);

  useEffect(() => {
    const onConsentUpdate = (event) => {
      if (event?.detail) {
        setPreferences(event.detail);
      }
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, onConsentUpdate);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, onConsentUpdate);
    };
  }, []);

  useEffect(() => {
    const onOpenPreferences = () => {
      setIsPreferencesOpen(true);
    };

    window.addEventListener(COOKIE_CONSENT_OPEN_EVENT, onOpenPreferences);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_OPEN_EVENT, onOpenPreferences);
    };
  }, []);

  const applyPreferences = (nextPrefs) => {
    const saved = saveCookieConsent(nextPrefs);
    setPreferences(saved);
    setIsBannerOpen(false);
    setIsPreferencesOpen(false);
  };

  const handleAcceptAll = () => {
    applyPreferences({
      analytics: true,
      functional: true,
      marketing: true,
    });
  };

  const handleRejectOptional = () => {
    applyPreferences({
      analytics: false,
      functional: false,
      marketing: false,
    });
  };

  const handleSaveCustom = () => {
    applyPreferences(preferences);
  };

  const updatePreference = (key) => (event) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: event.target.checked,
    }));
  };

  return (
    <>
      {isBannerOpen && !isPreferencesOpen && (
        <Slide in={isBannerOpen} direction="up" timeout={500} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: 'fixed',
              bottom: { xs: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', md: 20 },
              right: { xs: 16, md: 24 },
              left: { xs: 16, md: 'auto' },
              transform: 'none',
              zIndex: (theme) => theme.zIndex.modal - 1,
              width: { xs: 'auto', sm: 360, md: 380 },
              maxWidth: 400,
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.35)',
              background:
                'linear-gradient(122deg, rgba(19,48,63,0.92) 0%, rgba(21,88,111,0.92) 45%, rgba(32,112,104,0.92) 100%)',
              color: '#f7fbff',
              boxShadow: '0 26px 60px rgba(18, 49, 65, 0.35)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.2, color: '#f7fbff', mb: 1 }}>
                Your privacy choices
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(247,251,255,0.88)', lineHeight: 1.55, mb: 2.5 }}>
                We use required cookies to keep this site secure and optional cookies for analytics and feature
                improvements.
              </Typography>

              <Stack spacing={1.2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleAcceptAll}
                  sx={{
                    bgcolor: '#f7fbff',
                    color: '#0f4f63',
                    fontWeight: 700,
                    '&:hover': { bgcolor: '#ffffff' },
                  }}
                >
                  Accept all
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleRejectOptional}
                  sx={{
                    color: '#f7fbff',
                    borderColor: 'rgba(247,251,255,0.55)',
                    '&:hover': { borderColor: '#f7fbff', bgcolor: 'rgba(247,251,255,0.1)' },
                  }}
                >
                  Reject optional
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => setIsPreferencesOpen(true)}
                  sx={{
                    color: 'rgba(247,251,255,0.75)',
                    fontSize: '0.8125rem',
                    '&:hover': { color: '#f7fbff', bgcolor: 'rgba(247,251,255,0.08)' },
                  }}
                >
                  Manage preferences
                </Button>
              </Stack>
            </Box>
          </Box>
        </Slide>
      )}

      <Dialog
        maxWidth="sm"
        open={isPreferencesOpen}
        onClose={() => setIsPreferencesOpen(false)}
        TransitionComponent={TransitionUp}
      >
        <DialogTitle sx={{ pb: 1 }}>Cookie preferences</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which cookies you allow. Required cookies are always on because they support core security and
            authentication.
          </Typography>

          <Stack spacing={1.5}>
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Strictly necessary
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Required for login sessions, security and core functionality.
                  </Typography>
                </Box>
                <Switch checked disabled />
              </Stack>
            </Box>

            <Divider />

            <FormControlLabel
              control={<Switch checked={preferences.analytics} onChange={updatePreference('analytics')} />}
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Analytics cookies
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Helps us understand usage patterns and improve experience.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', m: 0 }}
            />

            <Divider />

            <FormControlLabel
              control={<Switch checked={preferences.functional} onChange={updatePreference('functional')} />}
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Functional cookies
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Saves preferences such as language or UI personalization.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', m: 0 }}
            />

            <Divider />

            <FormControlLabel
              control={<Switch checked={preferences.marketing} onChange={updatePreference('marketing')} />}
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Marketing cookies
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supports campaign measurement and remarketing.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', m: 0 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleRejectOptional} variant="outlined">
            Reject optional
          </Button>
          <Button onClick={handleAcceptAll} variant="outlined">
            Accept all
          </Button>
          <Button onClick={handleSaveCustom} variant="contained">
            Save choices
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CookieConsentBanner;
